import React, { useEffect, useRef, useState } from "react";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Box, SxProps, Theme } from "@mui/system";

interface Option {
  label: string;
  value: string;
}

interface AnimatedToggleButtonGroupProps {
  name?: string;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
}

const AnimatedToggleButtonGroup: React.FC<AnimatedToggleButtonGroupProps> = ({
  name,
  value,
  onChange,
  options,
  fullWidth = true,
  sx = {},
}) => {
  const groupRef = useRef<HTMLDivElement | null>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    const buttons = groupRef.current?.querySelectorAll(".MuiToggleButton-root");
    const index = options.findIndex((opt) => opt.value === value);
    if (buttons && buttons[index]) {
      const btn = buttons[index] as HTMLElement;
      const btnRect = btn.getBoundingClientRect();

      // No borderRadius here to avoid mismatch
      setIndicatorStyle({
        width: btnRect.width,
        height: btnRect.height,
        left: btn.offsetLeft,
        top: btn.offsetTop,
      });
    }
  }, [value, options]);

  return (
    <Box
      sx={{
        position: "relative",
        width: fullWidth ? "100%" : "auto",
        borderRadius: "6px",
        overflow: "hidden", // clip background cleanly
      }}
    >
      {/* Sliding blue background */}
      <Box
        sx={{
          position: "absolute",
          backgroundColor: "#1976d2",
          transition: "all 0.3s ease",
          zIndex: 1,
          borderRadius: 0, // ✅ remove radius to prevent white line
          ...indicatorStyle,
        }}
      />

      <ToggleButtonGroup
        ref={groupRef}
        value={value}
        exclusive
        onChange={(_, newVal) => {
          if (newVal !== null) onChange(newVal);
        }}
        fullWidth={fullWidth}
        sx={{
          position: "relative",
          zIndex: 2,
          border: "1px solid var(--opd-border-color, #ccc)",
          borderRadius: "6px",
          overflow: "hidden",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          display: "flex",
          justifyContent: "center",
          backgroundColor: "transparent",
          ...sx,
          "& .MuiToggleButton-root": {
            flex: { xs: "1 0 50%", sm: 1 },
            maxWidth: { xs: "50%", sm: "none" },
            textAlign: "center",
            textTransform: "none",
            color: "var(--opd-panel-fg, #333)",
            fontWeight: "bold",
            border: "none",
            borderRadius: 0,
            backgroundColor: "transparent",
            transition: "color 0.3s ease",
            position: "relative",

            // Mobile-specific borders
            "@media (max-width: 599.95px)": {
              border: "none",
              "&:nth-of-type(1), &:nth-of-type(2)": {
                borderBottom: "1px solid var(--opd-border-color, #ccc)",
              },
              "&:nth-of-type(1), &:nth-of-type(3)": {
                borderRight: "1px solid var(--opd-border-color, #ccc)",
              },
            },

            // Desktop borders
            "@media (min-width: 600px)": {
              "&:not(:last-of-type)": {
                borderRight: "1px solid var(--opd-border-color, #ccc)",
              },
            },

            "&.Mui-selected": {
              color: "#fff",
              zIndex: 3,
              border: "none !important",
            },
          },
        }}
      >
        {options.map((opt) => (
          <ToggleButton key={opt.value} value={opt.value}>
            {opt.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

export default AnimatedToggleButtonGroup;









